import React, { useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Sparkles, ArrowRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Check } from "lucide-react";
import { SparkleBurst } from "./Sparkle";
import { US_HOLIDAYS } from "../../lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_TYPES = [
    { id: "standard", label: "Standard Event", desc: "Party, celebration, or gathering · 2-hr minimum" },
    { id: "vendor", label: "Vendor Booth", desc: "Festival, school, fair · I bring the booth" },
    { id: "private", label: "Private Henna Session", desc: "One-on-one · per-design · non-bridal" },
];

const EVENT_TYPES = [
    "Kids Birthday Party",
    "Adult Birthday",
    "Baby Shower",
    "Eid / Diwali Party",
    "Corporate / Team Event",
    "Festival / Fair",
    "School Event / Carnival",
    "Other Celebration",
];

const SERVICES = [
    { id: "henna", label: "Henna" },
    { id: "face", label: "Face Painting" },
    { id: "glitter", label: "Glitter Tattoos" },
];

const GUESTS = ["1–15", "16–30", "31–60", "61–100", "100+"];

// minutes between two HH:MM times (handles same-day)
const minutesBetween = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    return mins;
};

// Weekday time restriction: Mon–Fri start_time must be 17:00 or later (5pm).
// Saturday & Sunday: any time. US holidays: any time.
const checkTimeAllowed = (date, startTime) => {
    if (!date || !startTime) return { ok: true, msg: "" };
    const iso = format(date, "yyyy-MM-dd");
    if (US_HOLIDAYS.includes(iso)) return { ok: true, msg: "" };
    const dow = date.getDay(); // 0 Sun … 6 Sat
    const isWeekday = dow >= 1 && dow <= 5;
    if (!isWeekday) return { ok: true, msg: "" };
    const [h] = startTime.split(":").map(Number);
    if (h < 17) return { ok: false, msg: "Weekday bookings start at 5:00 PM. Pick a later time, switch to a weekend, or choose a US holiday." };
    return { ok: true, msg: "" };
};

export const Inquiry = () => {
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [form, setForm] = useState({
        booking_type: "standard",
        is_nonprofit: false,
        name: "",
        email: "",
        phone: "",
        event_type: "",
        event_date: null,
        start_time: "",
        end_time: "",
        location: "",
        services: [],
        guest_count: "",
        message: "",
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const isKids = form.event_type?.toLowerCase().includes("kids");
    const isVendor = form.booking_type === "vendor";
    const isPrivate = form.booking_type === "private";

    const totalMinutes = useMemo(
        () => minutesBetween(form.start_time, form.end_time),
        [form.start_time, form.end_time]
    );
    const hoursDecimal = totalMinutes / 60;
    const hoursDisplay = totalMinutes
        ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60 ? `${totalMinutes % 60}m` : ""}`.trim()
        : "";

    const meetsMinimum = isPrivate ? totalMinutes > 0 : hoursDecimal >= 2;
    const timeCheck = useMemo(
        () => checkTimeAllowed(form.event_date, form.start_time),
        [form.event_date, form.start_time]
    );

    const toggleService = (id) =>
        setForm((f) => ({
            ...f,
            services: f.services.includes(id) ? f.services.filter((x) => x !== id) : [...f.services, id],
        }));

    // Validations per step
    const validStep1 = isPrivate
        ? Boolean(form.booking_type)
        : Boolean(form.booking_type) && form.event_type && form.services.length > 0;
    const validStep2 =
        Boolean(form.event_date) &&
        Boolean(form.location) &&
        Boolean(form.start_time) &&
        Boolean(form.end_time) &&
        meetsMinimum &&
        timeCheck.ok &&
        (isPrivate || Boolean(form.guest_count));
    const validStep3 = form.name && form.email;

    const submit = async () => {
        if (!validStep3) {
            toast.error("Please fill in your name and email.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                event_date: form.event_date ? format(form.event_date, "yyyy-MM-dd") : null,
                hours_total: parseFloat(hoursDecimal.toFixed(2)),
                is_kids_party: isKids,
                // For private henna, services is just henna
                services: isPrivate ? ["henna"] : form.services,
                event_type: isPrivate ? "Private Henna Session" : form.event_type,
            };
            const { data } = await axios.post(`${API}/inquiries`, payload);
            setDone(data);
            toast.success("Inquiry sent ✦", { description: "I'll be in touch within 48 hours." });
        } catch (e) {
            console.error(e);
            toast.error("Something went wrong", {
                description: e?.response?.data?.detail || "Please try again or email me directly.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <section id="inquiry" data-testid="inquiry-section" className="py-24 sm:py-32 bg-brand-parchment">
                <div className="max-w-2xl mx-auto px-6 sm:px-8 text-center">
                    <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}>
                        <CheckCircle2 className="h-16 w-16 mx-auto text-brand-coral mb-6" strokeWidth={1.4} />
                        <h2 className="font-display text-5xl sm:text-6xl text-brand-ink mb-5 leading-tight">
                            Your moment is in <em className="text-brand-coral">good hands</em>.
                        </h2>
                        <p className="text-lg text-brand-inkSoft mb-8 leading-relaxed">
                            Thank you, {form.name.split(" ")[0]}! I received your inquiry and will be in touch
                            within 24 hours with a custom quote.
                        </p>
                        <div className="text-xs text-brand-inkSoft tracking-wide" data-testid="inquiry-confirmation-id">
                            Reference: {done.id}
                        </div>
                    </motion.div>
                </div>
            </section>
        );
    }

    return (
        <section id="inquiry" data-testid="inquiry-section" className="py-24 sm:py-32 bg-brand-parchment relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-6 sm:px-8 relative">
                <div className="text-center mb-12">
                    <p className="text-xs tracking-[0.25em] uppercase text-brand-coral mb-4">Inquiry · book me</p>
                    <h2 className="font-display text-4xl sm:text-6xl text-brand-ink leading-[1.05]">
                        Tell me about your <em className="text-brand-coral">moment</em>.
                    </h2>
                    <p className="mt-4 text-brand-inkSoft max-w-xl mx-auto">
                        A 30-second form. I reply with a custom quote within 48 hours.
                    </p>
                </div>

                <div className="flex items-center justify-center gap-3 mb-10">
                    {[1, 2, 3].map((n) => (
                        <div key={n} className="flex items-center gap-3">
                            <div
                                className={`flex items-center justify-center h-8 w-8 rounded-full text-sm transition-all ${
                                    step >= n ? "bg-brand-coral text-white" : "bg-brand-border text-brand-inkSoft"
                                }`}
                                data-testid={`step-indicator-${n}`}
                            >
                                {n}
                            </div>
                            {n < 3 && <div className={`h-px w-12 ${step > n ? "bg-brand-coral" : "bg-brand-border"}`} />}
                        </div>
                    ))}
                </div>

                <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-3xl p-7 sm:p-10 border border-brand-border shadow-xl"
                >
                    {step === 1 && (
                        <div className="space-y-7">
                            <div>
                                <h3 className="font-display text-2xl text-brand-ink mb-1">What kind of booking?</h3>
                                <p className="text-sm text-brand-inkSoft">Pick the option that fits your event.</p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {BOOKING_TYPES.map((b) => {
                                    const active = form.booking_type === b.id;
                                    return (
                                        <button
                                            type="button"
                                            key={b.id}
                                            onClick={() => set("booking_type", b.id)}
                                            data-testid={`input-booking-type-${b.id}`}
                                            className={`text-left rounded-2xl border p-4 transition-all ${
                                                active
                                                    ? "border-brand-coral bg-brand-coral/5 shadow-sm"
                                                    : "border-brand-border hover:border-brand-coral/60"
                                            }`}
                                        >
                                            <div className={`text-sm font-medium ${active ? "text-brand-coral" : "text-brand-ink"}`}>
                                                {b.label}
                                            </div>
                                            <div className="text-xs text-brand-inkSoft mt-1 leading-relaxed">{b.desc}</div>
                                        </button>
                                    );
                                })}
                            </div>

                            {isVendor && (
                                <label
                                    className="flex items-start gap-3 rounded-2xl border border-brand-border p-4 cursor-pointer hover:border-brand-coral/60"
                                    data-testid="input-nonprofit-wrapper"
                                >
                                    <input
                                        type="checkbox"
                                        checked={form.is_nonprofit}
                                        onChange={(e) => set("is_nonprofit", e.target.checked)}
                                        className="mt-0.5 h-4 w-4 accent-brand-coral"
                                        data-testid="input-nonprofit"
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-brand-ink">This is a non-profit event</div>
                                        <div className="text-xs text-brand-inkSoft mt-0.5">
                                            Vendor fee is waived for verified non-profits.
                                        </div>
                                    </div>
                                </label>
                            )}

                            {!isPrivate && (
                                <>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Event type</Label>
                                        <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                                            <SelectTrigger className="mt-2 h-12 rounded-xl border-brand-border" data-testid="input-event-type">
                                                <SelectValue placeholder="Select event type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {EVENT_TYPES.map((t) => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">
                                            Services {isKids && <span className="text-brand-coral">✦ kids favorites!</span>}
                                        </Label>
                                        <p className="text-xs text-brand-inkSoft mt-1 mb-3">
                                            Pick one or combine any two. Combination pricing varies a bit — I'll quote you.
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            {SERVICES.map((s) => {
                                                const active = form.services.includes(s.id);
                                                const Wrapper = isKids ? SparkleBurst : "div";
                                                return (
                                                    <Wrapper key={s.id} className="block">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleService(s.id)}
                                                            data-testid={`input-service-${s.id}`}
                                                            className={`w-full px-5 py-4 rounded-2xl border text-left transition-all ${
                                                                active
                                                                    ? "bg-brand-coral text-white border-brand-coral shadow-md"
                                                                    : "bg-white border-brand-border hover:border-brand-coral text-brand-ink"
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2.5">
                                                                <span
                                                                    className={`inline-flex items-center justify-center h-4 w-4 rounded border transition-colors ${
                                                                        active ? "bg-white border-white" : "bg-transparent border-current"
                                                                    }`}
                                                                    aria-hidden
                                                                >
                                                                    {active && <Check className="h-3 w-3 text-brand-coral" strokeWidth={3} />}
                                                                </span>
                                                                <span className="text-sm font-medium">{s.label}</span>
                                                            </div>
                                                        </button>
                                                    </Wrapper>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}

                            {isPrivate && (
                                <div className="rounded-2xl bg-brand-coral/5 border border-brand-coral/20 p-5 text-sm text-brand-inkSoft leading-relaxed">
                                    Private henna sessions are priced per-design (non-bridal). I'll confirm the
                                    designs and total in my reply. See the per-design pricing in the
                                    <a href="#pricing" className="text-brand-coral underline underline-offset-4 mx-1">Booking & info</a>
                                    section.
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    disabled={!validStep1}
                                    onClick={() => setStep(2)}
                                    data-testid="btn-step-next-1"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-cream px-7 py-3 text-sm hover:bg-brand-coral transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Continue <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-7">
                            <h3 className="font-display text-2xl text-brand-ink">When and where?</h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Event date</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                data-testid="input-event-date"
                                                className="mt-2 w-full h-12 rounded-xl border border-brand-border bg-white px-4 text-left text-sm flex items-center justify-between hover:border-brand-coral transition-colors"
                                            >
                                                {form.event_date ? format(form.event_date, "PPP") : <span className="text-brand-inkSoft/70">Pick a date</span>}
                                                <CalendarIcon className="h-4 w-4 text-brand-inkSoft" />
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={form.event_date}
                                                onSelect={(d) => set("event_date", d)}
                                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Location / venue</Label>
                                    <Input
                                        value={form.location}
                                        onChange={(e) => set("location", e.target.value)}
                                        placeholder="City or venue name"
                                        data-testid="input-location"
                                        className="mt-2 h-12 rounded-xl border-brand-border"
                                    />
                                </div>

                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Start time</Label>
                                    <Input
                                        type="time"
                                        value={form.start_time}
                                        onChange={(e) => set("start_time", e.target.value)}
                                        data-testid="input-start-time"
                                        className="mt-2 h-12 rounded-xl border-brand-border"
                                    />
                                </div>

                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">End time</Label>
                                    <Input
                                        type="time"
                                        value={form.end_time}
                                        onChange={(e) => set("end_time", e.target.value)}
                                        data-testid="input-end-time"
                                        className="mt-2 h-12 rounded-xl border-brand-border"
                                    />
                                </div>

                                {!isPrivate && (
                                    <div className="sm:col-span-2">
                                        <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Guest count</Label>
                                        <Select value={form.guest_count} onValueChange={(v) => set("guest_count", v)}>
                                            <SelectTrigger className="mt-2 h-12 rounded-xl border-brand-border" data-testid="input-guest-count">
                                                <SelectValue placeholder="Approximate guest count" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {GUESTS.map((g) => <SelectItem key={g} value={g}>{g} guests</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            {/* Hours summary */}
                            {form.start_time && form.end_time && (
                                <div
                                    className={`flex items-center gap-3 rounded-2xl border px-5 py-4 ${
                                        meetsMinimum
                                            ? "border-brand-coral/30 bg-brand-coral/5"
                                            : "border-amber-300 bg-amber-50"
                                    }`}
                                    data-testid="hours-summary"
                                >
                                    {meetsMinimum ? (
                                        <Sparkles className="h-5 w-5 text-brand-coral shrink-0" />
                                    ) : (
                                        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                                    )}
                                    <div className="text-sm">
                                        <span className="font-medium text-brand-ink">Total: {hoursDisplay}</span>
                                        {!meetsMinimum && !isPrivate && (
                                            <span className="block text-amber-700 text-xs mt-0.5">
                                                A 2-hour minimum is required for standard events.
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Weekday-time restriction */}
                            {!timeCheck.ok && (
                                <div
                                    className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4"
                                    data-testid="time-restriction-warning"
                                >
                                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <div className="text-sm text-amber-800 leading-relaxed">{timeCheck.msg}</div>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <button type="button" onClick={() => setStep(1)} className="text-sm text-brand-inkSoft hover:text-brand-ink" data-testid="btn-step-back-2">
                                    ← Back
                                </button>
                                <button
                                    type="button"
                                    disabled={!validStep2}
                                    onClick={() => setStep(3)}
                                    data-testid="btn-step-next-2"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-cream px-7 py-3 text-sm hover:bg-brand-coral transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Continue <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <h3 className="font-display text-2xl text-brand-ink">How can I reach you?</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Your name</Label>
                                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="input-name" className="mt-2 h-12 rounded-xl border-brand-border" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Email</Label>
                                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="input-email" className="mt-2 h-12 rounded-xl border-brand-border" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Phone (optional)</Label>
                                    <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(925) 555-0100" data-testid="input-phone" className="mt-2 h-12 rounded-xl border-brand-border" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-xs uppercase tracking-widest text-brand-inkSoft">Tell me more (optional)</Label>
                                    <Textarea
                                        value={form.message}
                                        onChange={(e) => set("message", e.target.value)}
                                        placeholder="Themes, color palette, design preferences, dream vibes…"
                                        rows={4}
                                        data-testid="input-message"
                                        className="mt-2 rounded-xl border-brand-border"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-2">
                                <button type="button" onClick={() => setStep(2)} className="text-sm text-brand-inkSoft hover:text-brand-ink" data-testid="btn-step-back-3">
                                    ← Back
                                </button>
                                <SparkleBurst count={isKids ? 22 : 12}>
                                    <button
                                        type="button"
                                        onClick={submit}
                                        disabled={submitting || !validStep3}
                                        data-testid="btn-submit-inquiry"
                                        className="inline-flex items-center gap-2 rounded-full bg-brand-coral hover:bg-brand-coralLight text-white px-8 py-3.5 text-sm shadow-lg transition-all disabled:opacity-60"
                                    >
                                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                        {submitting ? "Sending…" : "Send my inquiry"}
                                    </button>
                                </SparkleBurst>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
};

export default Inquiry;
